/**
 * GÜNLÜK SERİYİ YÜKLE — Günlük Analiz grafiği ve ana sayfadaki getiri
 * özeti AYNI yolu kullanır (13.10.2026): önce bellekteki önbellek, sonra
 * şifreli kaptaki kalıcı kopya, kalanı motordan aylık parçalarla. Hesaplanan
 * günler iki önbelleğe de yazılır; yani ana sayfanın hesapladığı yıl grafiği
 * açınca yeniden hesaplanmaz (ve tersi).
 */

import type { IsoDate } from "@parafon/contracts";
import type { LocalRepository, ValuationSeriesPoint } from "@parafon/contracts/local-api";
import { kalicidanTamamla, onbellekDurumu, saklanabilirler, seriyiSakla } from "./seriOnbellegi";

/** Bir istekte en çok kaç gün (aylık parça). */
export const PARCA_GUN = 31;

export function gunEkle(date: IsoDate, gun: number): IsoDate {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + gun);
  return d.toISOString().slice(0, 10);
}

/** `from`..`to` aralığını en çok `PARCA_GUN` günlük parçalara böler. */
export function parcala(from: IsoDate, to: IsoDate): readonly (readonly [IsoDate, IsoDate])[] {
  const out: [IsoDate, IsoDate][] = [];
  let bas = from;
  while (bas <= to) {
    const son = gunEkle(bas, PARCA_GUN - 1);
    out.push([bas, son < to ? son : to]);
    bas = gunEkle(son, 1);
  }
  return out;
}

export interface SeriYukleSecenek {
  readonly bugun: IsoDate;
  /** true dönerse yükleme yarıda bırakılır ve `null` döner. */
  readonly iptal: () => boolean;
  /** Eksik gün sayısı belli olunca ve her parçadan sonra. */
  readonly ilerleme?: (biten: number, toplam: number) => void;
  /**
   * Yalnızca bu hesaplar (sanal hesap tek başına). Verilmezse bütün defter.
   * KAPSAMLI seri yalnızca BELLEKTE saklanır; şifreli kaptaki kalıcı kopya
   * bütün deftere aittir.
   */
  readonly accountIds?: readonly string[];
}

/** Bellekteki önbelleğin anahtarı: para birimi, kapsam varsa onunla. */
export function seriAnahtari(para: string, accountIds?: readonly string[]): string {
  return accountIds === undefined || accountIds.length === 0 ? para : `${para}#${[...accountIds].sort().join(",")}`;
}

/** `from`..`to` her gün, sırayla. İptal edilirse `null`. */
export async function seriYukle(
  repo: LocalRepository,
  para: string,
  from: IsoDate,
  to: IsoDate,
  secenek: SeriYukleSecenek,
): Promise<ValuationSeriesPoint[] | null> {
  const { bugun, iptal, ilerleme } = secenek;
  const kapsamli = secenek.accountIds !== undefined && secenek.accountIds.length > 0;
  const anahtar = seriAnahtari(para, secenek.accountIds);
  const birlestir = (
    elde: ReadonlyMap<IsoDate, ValuationSeriesPoint>,
    yeni: ReadonlyMap<IsoDate, ValuationSeriesPoint>,
  ): ValuationSeriesPoint[] => {
    const out: ValuationSeriesPoint[] = [];
    for (let d = from; d <= to; d = gunEkle(d, 1)) {
      const p = yeni.get(d) ?? elde.get(d);
      if (p !== undefined) out.push(p);
    }
    return out;
  };
  const ilk = onbellekDurumu(repo, anahtar, from, to, bugun);
  if (ilk.eksikler.length === 0) return birlestir(ilk.elde, new Map());
  ilerleme?.(0, ilk.eksikGun);
  // Bellekte olmayan günler önce şifreli kaptaki kalıcı kopyadan (sayfa
  // yeniden yüklendiyse hepsi oradadır); kalanı motordan.
  if (!kapsamli) await kalicidanTamamla(repo, para, ilk.eksikler, bugun);
  if (iptal()) return null;
  const { elde, eksikler, eksikGun } = onbellekDurumu(repo, anahtar, from, to, bugun);
  ilerleme?.(0, eksikGun);
  const yeni = new Map<IsoDate, ValuationSeriesPoint>();
  for (const [eBas, eSon] of eksikler) {
    for (const [bas, son] of parcala(eBas, eSon)) {
      const seri = await repo.valuationSeries({
        from: bas,
        to: son,
        reportCurrency: para,
        ...(kapsamli ? { accountIds: secenek.accountIds } : {}),
      });
      if (iptal()) return null;
      for (const p of seri.points) yeni.set(p.date, p);
      seriyiSakla(repo, anahtar, seri.points, bugun);
      const kalici = kapsamli ? [] : saklanabilirler(seri.points, bugun);
      if (kalici.length > 0) void repo.seriOnbellegi?.yaz(para, kalici).catch(() => undefined);
      ilerleme?.(yeni.size, eksikGun);
    }
  }
  return birlestir(elde, yeni);
}
